import React, { useEffect, useId, useRef, useState } from 'react';
import type { ProfileCardRarity } from '../../types/profileCard';
import {
  PROFILE_CARD_RARITY_COLORS,
  PROFILE_CARD_RARITY_LABEL,
  raritySelectOptions,
} from '../../utils/profileCardRarity';

type Props = {
  value: ProfileCardRarity | null;
  disabled?: boolean;
  'aria-label'?: string;
  onChange: (rarity: ProfileCardRarity) => void;
};

function rarityFg(rarity: ProfileCardRarity): string {
  return rarity === 'epic' ? '#1a1a1a' : '#ffffff';
}

/**
 * 등급 드롭다운 — 배경=등급색, 텍스트=흰색(노랑은 검정), 원형 도형은 흰 테두리.
 */
const ProfileCardRaritySelect: React.FC<Props> = ({
  value,
  disabled = false,
  'aria-label': ariaLabel = '카드 등급',
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const options = raritySelectOptions();
  const bg = value ? PROFILE_CARD_RARITY_COLORS[value] : null;
  const fg = value ? rarityFg(value) : null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <div
      ref={rootRef}
      className={`cp-pc-rarity-dd${open ? ' is-open' : ''}${value ? ` is-rarity-${value}` : ''}${
        disabled ? ' is-disabled' : ''
      }`}
    >
      <button
        type="button"
        className="cp-pc-rarity-dd-trigger"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        style={
          bg && fg
            ? { background: bg, color: fg, borderColor: bg }
            : undefined
        }
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
      >
        {bg ? (
          <span className="cp-pc-rarity-dot" style={{ background: bg }} aria-hidden />
        ) : null}
        <span className="cp-pc-rarity-dd-trigger-label">
          {value ? PROFILE_CARD_RARITY_LABEL[value] : '등급 선택'}
        </span>
        <span className="cp-pc-rarity-dd-chevron" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <ul id={listId} className="cp-pc-rarity-dd-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((o) => {
            const optionBg = PROFILE_CARD_RARITY_COLORS[o.value];
            const optionFg = rarityFg(o.value);
            const selected = value === o.value;
            return (
              <li key={o.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`cp-pc-rarity-dd-option${selected ? ' is-selected' : ''}`}
                  style={{
                    background: optionBg,
                    color: optionFg,
                    borderColor: optionBg,
                  }}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <span
                    className="cp-pc-rarity-dot"
                    style={{ background: optionBg }}
                    aria-hidden
                  />
                  {o.label}
                  {selected ? <span className="cp-pc-rarity-dd-check" aria-hidden>✓</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default ProfileCardRaritySelect;
